import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { SelectField, TextareaField, TextField } from '@/components/form-fields';
import { MediaPicker } from '@/components/media-picker';
import { useDetailTitle } from '@/components/page-header';
import { RichTextEditor } from '@/components/rich-text-editor';
import { EmptyState, ReadOnlyNotice, SectionCard } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import { deletePost, getPost, type PostRecord, postState, slugify, updatePost } from '@/lib/blog';
import { useForm } from '@/lib/use-form';

/**
 * One post.
 *
 * Publishing is a date, not a button: a post is on the site when its status
 * is published and its date has passed. Setting the date forward is how a
 * post is scheduled, which is why the control is a datetime rather than a
 * "publish now" toggle with a separate scheduler beside it.
 */
export const Route = createFileRoute('/blog/$postId')({
  component: BlogDetailPage,
});

const schema = z.object({
  title: z.string().trim().min(1, 'Give it a title').max(200),
  slug: z
    .string()
    .trim()
    .min(1, 'Needed for the address')
    .max(80)
    .regex(/^[a-z0-9-]+$/, 'Lower case, numbers and dashes'),
  excerpt: z.string().trim().max(400).optional(),
  body: z.string(),
  tags: z.string(),
  status: z.enum(['draft', 'published']),
  publishedAt: z.string(),
  seoTitle: z.string().trim().max(70).optional(),
  seoDescription: z.string().trim().max(200).optional(),
});

type Values = z.infer<typeof schema>;

/** `datetime-local` wants 'YYYY-MM-DDTHH:mm' in the browser's own zone. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';

  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;

  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toValues(post: PostRecord): Values {
  return {
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt ?? '',
    body: post.body ?? '',
    tags: post.tags.join(', '),
    status: post.status,
    publishedAt: toLocalInput(post.publishedAt),
    seoTitle: post.seo.title ?? '',
    seoDescription: post.seo.description ?? '',
  };
}

function BlogDetailPage() {
  const { postId } = useParams({ from: '/blog/$postId' });
  const { activeAccountId } = useAuth();
  const accountId = activeAccountId ?? '';

  const post = useQuery({
    queryKey: ['post', accountId, postId],
    queryFn: () => getPost(accountId, postId),
    enabled: Boolean(accountId),
  });

  useDetailTitle(post.data?.title ?? `Post ${postId.slice(0, 8)}`);

  if (post.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!post.data) {
    return (
      <EmptyState>
        No such post.{' '}
        <Link to="/blog" className="underline">
          Back to the blog
        </Link>
      </EmptyState>
    );
  }

  /*
   * Mounted with the record in hand rather than reset into afterwards -- the
   * same reason the discount editor is: a Radix Select whose controlled value
   * arrives after mount clears itself, and the failure is silent.
   */
  return <PostForm accountId={accountId} post={post.data} />;
}

function PostForm({ accountId, post }: { accountId: string; post: PostRecord }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canManage = can('cms.manage');

  const [cover, setCover] = useState<{ url: string | null; assetId: string | null }>({
    url: post.coverUrl,
    assetId: post.coverMediaId,
  });

  /*
   * Whether the address has stopped following the title.
   *
   * It has to be state rather than a test against the current slug: comparing
   * the slug to its generated shape only holds until the first keystroke --
   * typing "Harvest week" set the address to "h" and then stopped, because by
   * the second character the slug no longer looked auto-generated. It locks
   * when a person edits the address themselves, and starts locked for
   * anything already published, whose URL other people may have linked to.
   */
  const [slugLocked, setSlugLocked] = useState(
    post.status !== 'draft' || !post.slug.startsWith('untitled-post'),
  );

  const { values, errors, isDirty, set, reset, submit } = useForm<Values>(toValues(post), schema);

  const save = useMutation({
    mutationFn: (parsed: Values) =>
      updatePost(accountId, post.id, {
        title: parsed.title,
        slug: parsed.slug,
        excerpt: parsed.excerpt || null,
        body: parsed.body,
        coverUrl: cover.url,
        coverMediaId: cover.assetId,
        tags: parsed.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        status: parsed.status,
        // Publishing with no date set means "now", which is what a person
        // pressing publish means; scheduling is the same field, set forward.
        publishedAt:
          parsed.status === 'published'
            ? new Date(parsed.publishedAt || Date.now()).toISOString()
            : parsed.publishedAt
              ? new Date(parsed.publishedAt).toISOString()
              : null,
        seo: {
          ...(parsed.seoTitle ? { title: parsed.seoTitle } : {}),
          ...(parsed.seoDescription ? { description: parsed.seoDescription } : {}),
        },
      }),
    onSuccess: () => {
      toast.success('Saved.');
      queryClient.invalidateQueries({ queryKey: ['post', accountId, post.id] });
      queryClient.invalidateQueries({ queryKey: ['posts', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: () => deletePost(accountId, post.id),
    onSuccess: () => {
      toast.success('Post deleted.');
      queryClient.invalidateQueries({ queryKey: ['posts', accountId] });
      navigate({ to: '/blog' });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const state = postState({
    ...post,
    status: values.status,
    publishedAt: values.publishedAt ? new Date(values.publishedAt).toISOString() : null,
  });

  return (
    <form onSubmit={submit((parsed) => save.mutate(parsed))} noValidate className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <Badge variant={state === 'published' ? 'default' : 'secondary'}>
            {state === 'published' ? 'Live' : state === 'scheduled' ? 'Scheduled' : 'Draft'}
          </Badge>
          {isDirty && (
            <Button type="button" variant="ghost" onClick={() => reset()}>
              Discard
            </Button>
          )}
          <Button type="submit" disabled={!canManage || !isDirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {!canManage && <ReadOnlyNotice permission="cms.manage" />}

      <fieldset disabled={!canManage} className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-6">
            <SectionCard title="Post">
              <TextField
                id="post-title"
                label="Title"
                value={values.title}
                error={errors.title}
                onChange={(value) => {
                  set('title', value);
                  if (!slugLocked) set('slug', slugify(value));
                }}
              />
              <TextField
                id="post-slug"
                label="Address"
                description="Shown at /blog/<address>."
                value={values.slug}
                error={errors.slug}
                onChange={(value) => {
                  setSlugLocked(true);
                  set('slug', value);
                }}
              />
              <TextareaField
                id="post-excerpt"
                label="Excerpt"
                description="The summary the index and any share card use."
                rows={3}
                value={values.excerpt ?? ''}
                error={errors.excerpt}
                onChange={(value) => set('excerpt', value)}
              />

              <div className="space-y-2">
                <span className="text-sm font-medium">Body</span>
                <RichTextEditor
                  accountId={accountId}
                  value={values.body}
                  onChange={(html) => set('body', html)}
                  disabled={!canManage}
                  placeholder="Write the post…"
                />
              </div>
            </SectionCard>
          </div>

          <div className="space-y-6">
            <SectionCard title="Publishing">
              <SelectField
                id="post-status"
                label="Status"
                value={values.status}
                onChange={(value) => set('status', value as Values['status'])}
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'published', label: 'Published' },
                ]}
              />
              <TextField
                id="post-date"
                label="Publish date"
                type="datetime-local"
                description="Set it forward to schedule; the storefront hides a post until then."
                value={values.publishedAt}
                onChange={(value) => set('publishedAt', value)}
              />
              <TextField
                id="post-tags"
                label="Tags"
                description="Comma separated. The index filters on these."
                value={values.tags}
                onChange={(value) => set('tags', value)}
              />
            </SectionCard>

            <SectionCard title="Cover" description="Shown on the index and at the top of the post.">
              <MediaPicker
                accountId={accountId}
                value={cover.url}
                folder="content"
                disabled={!canManage}
                onChange={(picked) =>
                  setCover({ url: picked?.url ?? null, assetId: picked?.assetId ?? null })
                }
              />
            </SectionCard>

            <SectionCard title="SEO" description="Falls back to the title and excerpt.">
              <TextField
                id="post-seo-title"
                label="Title"
                value={values.seoTitle ?? ''}
                error={errors.seoTitle}
                onChange={(value) => set('seoTitle', value)}
              />
              <TextareaField
                id="post-seo-description"
                label="Description"
                rows={3}
                value={values.seoDescription ?? ''}
                error={errors.seoDescription}
                onChange={(value) => set('seoDescription', value)}
              />
            </SectionCard>

            {canManage && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                <Trash2 className="size-4" />
                {remove.isPending ? 'Deleting…' : 'Delete post'}
              </Button>
            )}
          </div>
        </div>
      </fieldset>
    </form>
  );
}
